/**
 * Dashboard — the Overview screen (Anna's main view).
 *
 * Ported from app.jsx (Dashboard component) in the Claude Design output.
 * Production defaults: showBenchmark=true, ornaments=true (from TWEAK_DEFAULTS).
 * The Claude Design tweaks panel is NOT included — these are hardcoded defaults.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
// ── ONE SHELL (cohesion pass phase 3, 2026-09-15) ────────────────────────────
//
// This file used to hand-roll the portal shell: its own `.app` div, its own
// `<Sidebar/>`, its own backdrop, its own topbar, its own search modal and its
// own notification popover, all copied from `ScreenShell` and then left to
// drift. GAP-ANALYSIS section 2 names that copy "the single largest cause of
// the portal looking unlike itself page to page", and the drift was real and
// repeatedly expensive:
//
//   · `useSyncMyTenants` + `<TenantSwitcher/>` landed in ScreenShell on
//     2026-08-06 and reached this file on 2026-08-08, two days during which the
//     workspace switcher was invisible on the one screen every client lands on.
//   · The SKIP LINK never reached it at all. `ScreenShell` has rendered
//     `<SkipToContentLink/>` and `<main id="main" tabIndex={-1}>` since the
//     2026-08-22 a11y sweep; this shell rendered a bare `<main>`, so WCAG 2.4.1
//     bypass-blocks was satisfied everywhere in the portal EXCEPT /app/dashboard.
//   · The "Back to admin console" banner (`?from=admin`) was ScreenShell-only,
//     so an operator who entered the portal from the console had no way back
//     from the screen they landed on.
//
// Everything the topbar carried is now one copy in `ScreenShell`: the bell and
// its popover, search, the tenant switcher, the light/dark toggle, the
// hamburger and the drawer. The three greeting slots below are all this screen
// still owns of its chrome.
import { ScreenShell } from '../screens/screen-shell';
import { lazy, Suspense } from 'react';
import { Hero } from './hero';
import { RegistrationJourney } from './registration-journey';
import { EstateAssetsCard } from './estate-assets-card';
import { EstateSplitDonuts } from './estate-split-donuts';
import { SectionErrorBoundary } from '../screens/section-error-boundary';
import { ficaDaysOutOf } from '../../lib/fica-review';
// Lazy-split the display-currency card so it (and the lib/currency formatter
// path it pulls in via this eager route) lands in its own async chunk —
// keeps the eager app bundle under the 200 kB size-limit budget (#43).
const DisplayTotalCard = lazy(() =>
  import('./display-total-card').then((m) => ({ default: m.DisplayTotalCard })),
);
import { AllocationCard } from './allocation-card';
import { WealthDistribution } from './wealth-distribution';
import { QuickActions } from './quick-actions';
import { HoldingsTable } from './holdings-table';
import { ValuesAttentionCard } from './values-attention-card';
import { DistributionRhythm } from './distribution-rhythm';
import { ActivityFeed } from './activity-feed';
import { TrusteesCard } from './trustees-card';
import { BeneficiariesMini } from './beneficiaries-mini';
import { ResolutionBanner } from './resolution-banner';
import { trustLetterheadLine } from './dashboard-letterhead';
import {
  useDashboardSummary,
  useDashboardHoldings,
  useResolutions,
  useMe,
  useTrusts,
  useTrustSurfaces,
  useTenantBrandingBySlug,
} from '../../lib/api';
import { resolveTenantSlugFromHost } from '../../lib/tenant';
import { useActAsClientName } from '../../lib/acting/context';
// POR-01 (M1 §H): "where am I the founder, where am I a trustee, where am I a
// beneficiary" across every trust. The component already existed and was
// mounted on ONE screen — the profile — so the dashboard, which is where a
// client with several trusts actually lands, never showed the distribution.
// Mounted, not rewritten: one component, two homes.
import { YourTrustsCard } from '../trusts/your-trusts-card';
// POR-09 (M1 §G): the provisional-tax countdown Paul found "buried low on the
// page". These are calendar facts computed locally, so no endpoint is needed;
// the module's own honesty rule (a SARS date that has passed reads "was due",
// never "overdue") is what makes it safe to put at the top of the page.
import {
  statutoryCalendar,
  obligationBadgeLabel,
  daysUntil,
  deadlinePhrase,
} from '../../lib/statutory-calendar';
// COHESION PASS (2026-09-15) — the two shared primitives this screen proves.
// `ConsoleBand` shipped on the firm console in August and the portal never got
// it, which is the named reason the client dashboard reads as a wall of
// equal-weight boxes; `EntityRow` is the console queue row, promoted to both
// shells. Row 0 below is the contract's own worked example of the change.
import { ConsoleBand } from '../admin/console-band';
import { EntityRow } from '../entity-row';
import { displayLegalName } from '../../lib/format/legal-name';
import { StateCard } from '../state-card';
import { displayPersonName } from '../../lib/format/person-name';

/**
 * Production defaults once handled by the Claude Design tweaks panel.
 * Inlined locally (no fixture dependency) so this file ships without any
 * `fake-data/anna` reference.
 */
const TWEAK_DEFAULTS = {
  showBenchmark: true,
  ornaments: true,
} as const;

export function Dashboard() {
  // Live counts for the greeting. The banner uses the same queryKey, so this
  // is a cache-shared read with no extra round-trip.
  const summaryQ = useDashboardSummary();
  const summary = summaryQ.data;
  // TRS-03: the ONE read of the FICA clock on this screen. `ficaDaysOutOf`
  // returns undefined when no initial FICA is on file (or the server sent no
  // countdown), so every branch below tests undefined rather than defaulting a
  // missing clock to a number — `?? 0` renders as "due today".
  const reviewDaysOut = summary ? ficaDaysOutOf(summary) : undefined;
  const awaitingQ = useResolutions({ awaitingMySignature: true });
  const awaitingData = awaitingQ.data;
  const awaitingCount = awaitingData?.resolutions.length ?? 0;
  // DESIGN GATE A2 — a zero before the query settles reads as a defect. This
  // band said "Nothing awaits your signature · FICA review in … days" for as
  // long as the API took to answer, which on a cold Function App is ten
  // seconds of an all-clear the app had not yet checked. While either read is
  // pending the band says it is still reading.
  const statusPending = summaryQ.isPending || awaitingQ.isPending;

  // Pulled to keep the me/profile cache warm for the shared shell — the
  // greeting itself no longer uses a "Welcome back, {name}" pattern (Phase 5
  // Task 5.5: fiduciary-grade microcopy leads with fact-led status).
  const meQ = useMe();
  void meQ;

  // F-FIRM-014 (Batch 16, 2026-05-20) — white-label rendering pipeline:
  // dashboard footer tagline pulls from the host tenant's branding overlay
  // (falls back to "Quiet rigour for fiduciary work." when no overlay).
  // Reads from the cache populated at routes/__root.tsx.
  const hostSlug = resolveTenantSlugFromHost(
    typeof window !== 'undefined' ? window.location.host : '',
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : undefined,
  );
  const brandingQ = useTenantBrandingBySlug(hostSlug);
  const tenantTagline =
    brandingQ.data?.branding.tagline ?? 'Quiet rigour for fiduciary work.';
  // White-label safe: surface the firm's own name in the footer signature
  // (never the Elreign domain). Empty when no overlay so no bare separator.
  const tenantFirmName = brandingQ.data?.branding.firmName ?? '';

  // Posture (2026-08-22): when a firm is administering this client, empty-state
  // copy names the CLIENT instead of saying "your trust" to the operator.
  const actAsClientName = useActAsClientName();
  // "What you'll need" inline help on the setup CTA (2026-08-22 meeting).
  const [setupHelpOpen, setSetupHelpOpen] = useState(false);

  // Footer signature pulls from the first trust the caller is a member of.
  // The same `trustId` feeds the four per-trust dashboard cards below so
  // they no longer fall back to the Anna fixture.
  const trustsQ = useTrusts();
  const trustsCount = trustsQ.data?.trusts.length ?? 0;
  const firstTrust = trustsQ.data?.trusts[0];
  const trustId = firstTrust?.id;
  // TRUSTEE-09: only treat "no trust" as actionable once the trusts query has
  // actually settled — a loading state is not an empty state. "Settled" =
  // no longer loading (either it returned data, or it errored). When there's
  // genuinely no trust (also the case that leaves useDashboardSummary 404ing
  // into em-dashes), surface an explicit "set up your trust" CTA rather than
  // a row of "—".
  const trustsSettled = !trustsQ.isLoading;
  // F-032 / act-as F5: a FAILED /v1/trusts is NOT an empty state. Under a firm
  // act-as session (or any transient fetch failure) trustsQ can error; without
  // this guard trustsCount stays 0 and the header renders a confident
  // "No trusts under administration yet" + "Set up your trust" CTA OVER the
  // failure — masquerading a load error as genuinely-empty. Mirror the honest
  // error+retry pattern holdingsQ already uses below. Error takes precedence
  // over the empty state; a true empty state is only "settled AND not errored".
  const trustsErrored = trustsQ.isError;
  const hasNoTrust = trustsSettled && !trustsErrored && trustsCount === 0;

  // ── What this caller may READ of the trust in scope (2026-08-18) ──────────
  // Tiered per-trust read access shipped across 37 handlers. This dashboard is
  // where an individual LANDS, and for a beneficiary-tier member nearly every
  // card on it — the hero, holdings, allocation, wealth distribution, the
  // trustee and beneficiary minis, the activity feed — is now a 403. Left
  // alone it would be the first thing they see and it would be broken.
  //
  // Each of those is gated below on the surface its endpoint serves, and the
  // queries behind them are DISABLED rather than allowed to fail. What remains
  // is a real dashboard: their own estate assets, their own trust's identity
  // and registration progress, and their own distributions.
  const { can } = useTrustSurfaces(trustId);
  const canFinancials = can('financials');
  const canPeople = can('people');
  const canGovernance = can('governance');
  const canDistributionsAll = can('distributions-all');

  // Holdings — live data per trust. Renders skeleton / error / empty inside
  // the card body so the surrounding chrome stays consistent.
  const holdingsQ = useDashboardHoldings(trustId, { enabled: canFinancials });
  const holdingsAsAt = holdingsQ.data?.asAt
    ? new Date(holdingsQ.data.asAt).toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  const holdingsCount =
    holdingsQ.data?.byAssetClass.reduce((a, b) => a + b.holdings.length, 0) ?? 0;

  // Day-1 "quiet furniture" collapse (Paul-decided 2026-07-11). Before any
  // asset registers to the trust, the investment-reporting surface (the display-
  // total card, the allocation card, the holdings table, and the 8-quarter
  // distribution rhythm) is all zeros — a wall of empty cards that reads as a
  // broken product on a client's very first visit. While there are genuinely no
  // holdings, those four sections collapse into ONE honest line; the moment a
  // holding exists they all render in full again (nothing is deleted). Gated on
  // a SETTLED empty result so loading skeletons + error states are untouched.
  const holdingsSettled =
    !holdingsQ.isLoading && !holdingsQ.isError && holdingsQ.data !== undefined;
  // Two different reasons to collapse the same surface, and the copy must not
  // conflate them: "there is nothing yet" (day one) versus "this is not yours
  // to see" (a beneficiary). Telling a beneficiary that reporting "unlocks when
  // assets register" would be a false promise about a trust that may already
  // hold millions.
  const collapseForDayOne = !!trustId && holdingsSettled && holdingsCount === 0;
  const collapseForTier = !!trustId && !canFinancials;
  const collapseInvestment = collapseForDayOne || collapseForTier;

  // POR-09 — the SARS deadlines that belong in the critical band.
  //
  // The band shipped in August admitted exactly two things: resolutions
  // awaiting a signature, and a FICA review inside fourteen days. The alert the
  // row actually names, "provisional tax due in 9 days", was not one of them:
  // it lives on StatutoryCalendarCard, which this dashboard never mounts. So
  // the band was at the top of the page and the buried notice was still buried.
  //
  // Same fourteen-day window as the FICA line beside it, so "critical" means one
  // thing on this band. `fica-review` is dropped because `summary.ficaDaysOut`
  // already renders it a few elements later and two clocks for one obligation is
  // how a reader stops trusting either. Nothing renders without a trust: SARS
  // dates are facts about a trust, not about an empty account.
  const CRITICAL_DAYS = 14;
  /* One instant for the whole render. Two rows reading Date.now() separately
     can land either side of midnight and disagree about what day it is, which
     on a deadline chip is the difference between "Due today" and "Overdue by
     1 day". */
  const nowMs = Date.now();
  const criticalObligations =
    trustId && canGovernance
      ? statutoryCalendar().filter(
          (o) =>
            o.id !== 'fica-review' &&
            o.daysRemaining >= 0 &&
            o.daysRemaining <= CRITICAL_DAYS,
        )
      : [];

  /* The band meta counts what is actually rendered below it, so the label and
     the rows cannot disagree. Counted here rather than in the JSX because the
     guard that decides whether the band renders at all reads the same three
     sources. */
  /* The band meta for "The book". A COUNT, so design gate A2 applies to it as
     much as to the card it labels: while the holdings read is in flight, or
     once it has failed, the band says nothing rather than saying nought. */
  const bookBandNote =
    holdingsQ.isLoading || holdingsQ.isError || holdingsCount === 0
      ? undefined
      : `${holdingsCount} holding${holdingsCount === 1 ? '' : 's'}`;

  const needsYouCount =
    awaitingCount +
    (reviewDaysOut !== undefined && reviewDaysOut <= CRITICAL_DAYS ? 1 : 0) +
    criticalObligations.length;

  // ── THE GREETING IS ALL OF THE CHROME THIS SCREEN STILL OWNS ─────────────
  //
  // `ScreenShell` supplies the `.topbar`, the `.greeting` wrapper, the `<h1>`
  // and the `.sub` div. These three values are the slots it leaves open, built
  // here rather than inline so the shell call below stays readable.
  //
  // The eyebrow is CONDITIONAL, which is why `ScreenShell`'s `eyebrow` prop
  // became optional in this pass. 2026-08-25 visual sweep: the "Updated {time}"
  // pill and live-dot were the CLIENT-portal twin of the admin-dashboard date
  // stamps Paul ordered removed twice ("no date or time stamps anywhere on the
  // dashboard") and the admin removal never reached this shell. Gone the same
  // way, live-dot included. The DAY ONE posture chip stays: it is an account
  // state, not a timestamp.
  const greetingEyebrow = !summary?.lastUpdated ? 'Day one' : undefined;

  const greetingTitle = (
    <>
      {/* F3 (firm-rehearsal 2026-07-11, P2 #17): this used to key off
          raw `trustsCount` (0 while trustsQ is simply still loading,
          same as a genuine empty book), so right after commit / act-as
          re-entry the H1 could flash the FULL "No trusts under
          administration yet" empty state over a hero card that was
          already showing a committed trust — two endpoints
          disagreeing transiently until /v1/trusts settles. The
          sub-copy below already guarded on `hasNoTrust`
          (trustsSettled && !trustsErrored && trustsCount===0); the
          headline now shares that exact guard, plus an explicit
          neutral loading state for the "not settled yet" gap the raw
          count silently mapped onto "empty". */}
      {/* Roman, unpunctuated (same rule as the settled states below): neither
          transient state names an entity, so neither gets the serif italic
          or a trailing stop. `<em>` carried both by inheritance from an
          earlier version of this block, not by design. */}
      {trustsErrored ? (
        <>Couldn&rsquo;t load your trusts</>
      ) : !trustsSettled ? (
        <span data-testid="trusts-loading-heading" aria-busy="true">
          Checking your trusts&hellip;
        </span>
      ) : hasNoTrust ? (
        <>No trusts under administration yet</>
      ) : trustsCount === 1 ? (
        /* "in good standing" removed 2026-08-22 (meeting ruling): the
           phrase was appended off a bare COUNT, asserting a compliance
           state nothing had checked — trusts with every FICA document
           outstanding read as "in good standing" minutes after
           creation. "Under administration" states what is actually
           known; compliance state belongs to the FICA line below.
           Roman, unpunctuated (design-handoff cohesion pass canvas 1a,
           2026-09-15): a heading is not a sentence, and "under
           administration" names no entity, so it takes no <em> and no
           trailing stop. */
        <><b>1 trust</b> under administration</>
      ) : (
        <><b>{trustsCount} trusts</b> under administration</>
      )}
    </>
  );

  // The four `.sub` branches. Each carried its own `<div className="sub">` when
  // this file drew its own topbar; the test ids moved onto a `<span>` so the
  // shell owns the one `.sub` div and `getByTestId` still finds each state.
  const greetingSub = (
    trustsErrored ? (
      <span data-testid="trusts-error-state">
        We couldn&rsquo;t reach the server to load your trusts.{' '}
        <button
          type="button"
          data-testid="trusts-retry-cta"
          onClick={() => void trustsQ.refetch()}
          style={{ color: 'var(--primary-700)', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2, font: 'inherit' }}
        >
          Try again
        </button>
        .
      </span>
    ) : hasNoTrust ? (
      <span data-testid="no-trust-empty-state">
        {/* Posture-aware copy (2026-08-22 meeting ruling): when a FIRM
            is administering this client, "your trust" is wrong twice —
            it isn't the operator's trust, and the client has a name.
            Name the client; keep second person only for the individual
            posture. */}
        {actAsClientName ? (
          <>
            {displayPersonName(actAsClientName)} has no trust under administration yet.{' '}
            {/* ARC-01 (grader, 5 Sep 2026, P0): a firm acting for a client
                was sent into the CLIENT's own individual wizard and stopped
                at the operator's authenticator step. The firm's path for a
                trust-less client is the structure builder, the same place
                "Map structure" on the Client Book leads. */}
            <Link
              to="/app/structure"
              data-testid="set-up-trust-cta"
              style={{ color: 'var(--primary-700)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}
            >
              Set up {displayPersonName(actAsClientName)}
            </Link>{' '}
            to get started.
          </>
        ) : (
          <>
            You don&rsquo;t have a trust under administration yet.{' '}
            <Link
              to="/onboarding/individual"
              data-testid="set-up-trust-cta"
              style={{ color: 'var(--primary-700)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}
            >
              Set up your trust
            </Link>{' '}
            to get started.
          </>
        )}
        {/* 2026-08-22 (meeting, Beno): an "i" beside trust setup telling
            people WHAT the step needs — for users without their trust
            papers to hand. Plain-language, dismissible on re-click. */}
        <button
          type="button"
          aria-label="What you'll need to set up a trust"
          aria-expanded={setupHelpOpen}
          onClick={() => setSetupHelpOpen((o) => !o)}
          style={{
            marginLeft: 8,
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '1px solid var(--hairline-2)',
            background: 'transparent',
            color: 'var(--ink-3)',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
            cursor: 'pointer',
            verticalAlign: 'middle',
          }}
        >
          i
        </button>
        {setupHelpOpen && (
          <span
            role="note"
            style={{
              display: 'block',
              marginTop: 8,
              fontSize: 12.5,
              color: 'var(--ink-3)',
              lineHeight: 1.6,
              maxWidth: '52ch',
            }}
          >
            Setting up a trust means uploading its documents: the trust deed, the
            Master&rsquo;s Letter of Authority if issued, and ID plus proof of address
            for each founder, trustee and beneficiary. Elreign reads them and builds
            the record. Nothing needs to be re-typed, and you can add missing
            documents later.
          </span>
        )}
      </span>
    ) : statusPending ? (
      // A2: still reading. Not "nothing outstanding", not a zero.
      <span data-testid="dashboard-status-loading">
        Checking your signatures and FICA status&hellip;
      </span>
    ) : (
      <>
        {awaitingCount === 0 ? (
          <>Nothing awaits your signature</>
        ) : awaitingCount === 1 ? (
          <><b>1 resolution</b> needs your signature</>
        ) : (
          <><b>{awaitingCount} resolutions</b> need your signature</>
        )}{' '}
        {/* TRS-03: no review clock before an initial FICA exists. The
            backend now withholds the countdown as well as answering the
            flag, so there is no number left to print here even if this
            guard were removed — and no "… days" placeholder either. */}
        {summary?.initialFicaOnFile === false ? (
          <>&middot; <b>Initial FICA outstanding</b></>
        ) : reviewDaysOut !== undefined ? (
          <>&middot; FICA review in <b>{reviewDaysOut} days</b></>
        ) : null}
      </>
    )
  );

  return (
    <ScreenShell
      activeNav="overview"
      eyebrow={greetingEyebrow}
      title={greetingTitle}
      sub={greetingSub}
    >
        {/* XCUT-13: each independent section is wrapped in a SectionErrorBoundary
            so a render throw in one card is contained — it shows a compact
            "couldn't load · your data is safe · retry" fallback in place rather
            than tearing down the whole dashboard. Multi-column rows are wrapped
            as a single boundary so the boundary wrapper doesn't disturb the CSS
            grid columns; full-width cards are wrapped individually. */}

        {/* Row 0: Critical actions (2026-08-22 meeting): urgent items must sit
            at the TOP of the dashboard, not buried down-page — the provisional-
            tax alert was found "hidden within the lower layout". Built from
            data already on this page; renders nothing when nothing is urgent.

            COHESION PASS (2026-09-15) — this is the pass's own worked example,
            and the proof screen for ConsoleBand and EntityRow on the portal.
            It was one red-left-bordered box with up to five facts run together
            on a single wrapping 13.5px line ("2 resolutions awaiting your
            signature   FICA review due in 9 days   Provisional tax due in 6
            days"), which is a sentence, not a worklist: nothing was clickable,
            nothing said which trust, and the eyebrow shouted ACTION REQUIRED
            in red without saying what to do.

            It is now the band "Needs you" over the shared row: a mono
            reference, the trust name in italic serif because it is a named
            thing, what it is, and ONE dated chip. The red left marker carries
            the severity, so the chip can state the deadline instead of
            shouting a severity a second time. The resolution rows are real
            links to the signing screen, which is the two-click reachability
            design gate B1 asks for and the old strip did not offer at all. */}
        {(awaitingCount > 0 ||
          (reviewDaysOut !== undefined && reviewDaysOut <= CRITICAL_DAYS) ||
          criticalObligations.length > 0) && (
          <SectionErrorBoundary label="Critical actions">
            <ConsoleBand
              id="needs-you"
              title="Needs you"
              note={
                needsYouCount === 1 ? '1 item needs you' : `${needsYouCount} items need you`
              }
            >
              <section
                className="card"
                data-testid="critical-actions"
                aria-label="Critical actions"
              >
                {/* Resolutions first: the only rows here a reader can finish in
                    one click. `deadline` is optional on the wire, so a
                    resolution with no date gets no chip rather than a computed
                    zero — design gate A2, and the same rule as the FICA clock
                    two lines down. */}
                {(awaitingData?.resolutions ?? []).map((r) => {
                  const days =
                    r.deadline !== undefined ? daysUntil(r.deadline.slice(0, 10), nowMs) : undefined;
                  const tone = days === undefined ? 'routine' : days < 0 ? 'overdue' : 'soon';
                  return (
                    <EntityRow
                      key={r.id}
                      testId={`needs-you-${r.id}`}
                      reference={r.ref}
                      name={displayLegalName(r.trustName) || 'This trust'}
                      description={`${r.title} · awaiting your signature`}
                      {...(days !== undefined ? { urgency: deadlinePhrase(days) } : {})}
                      tone={tone}
                      to="/app/resolutions/$id/sign"
                      params={{ id: r.id }}
                    />
                  );
                })}
                {/* The FICA review. `reviewDaysOut` is undefined when no initial
                    FICA is on file (TRS-03), and the guard above already means
                    it is inside the fourteen-day window when it renders. */}
                {reviewDaysOut !== undefined && reviewDaysOut <= CRITICAL_DAYS && (
                  <EntityRow
                    testId="needs-you-fica"
                    reference="FICA-REV"
                    name={displayLegalName(summary?.trustName) || 'This trust'}
                    description="Periodic FICA review"
                    urgency={deadlinePhrase(reviewDaysOut)}
                    tone={reviewDaysOut < 0 ? 'overdue' : 'soon'}
                  />
                )}
                {/* POR-09 — the provisional-tax countdown, at the top where Paul
                    asked for it. `obligationBadgeLabel` is the same wording the
                    statutory card uses, so the two never disagree, and it is
                    the honest one: a SARS date the product cannot observe reads
                    "Was due", never "overdue". */}
                {criticalObligations.map((o) => (
                  <EntityRow
                    key={o.id}
                    testId={`critical-obligation-${o.id}`}
                    reference="SARS"
                    name={displayLegalName(summary?.trustName) || 'This trust'}
                    description={o.label}
                    urgency={obligationBadgeLabel(o)}
                    tone={o.daysRemaining <= 2 ? 'overdue' : 'soon'}
                  />
                ))}
              </section>
            </ConsoleBand>
          </SectionErrorBoundary>
        )}

        <ConsoleBand id="the-estate" title="The estate">
          {/* Row 1: Hero — full width. `financials`: the hero IS the corpus
              (assets under administration, year-on-year movement). A beneficiary
              reads none of it — the BeneficiaryHero on the trust overview is
              where they are told what they can see instead. */}
          {canFinancials ? (
            <SectionErrorBoundary label="Portfolio summary">
              <Hero showBenchmark={TWEAK_DEFAULTS.showBenchmark} />
            </SectionErrorBoundary>
          ) : null}

          {/* Row 1-split: personal vs trust (Group 3, "tell the truth on the
              dashboard" — 1 August 2026 Paul/Beno meeting,
              tasks/beno-meeting-20260801/IMPLEMENTATION-PLAN.html). Placed
              directly under the hero's headline number on purpose: it is the
              immediate, honest recontextualisation of that combined figure —
              a discretionary trust is a separate legal person, so its assets
              are not part of an individual's net worth. Tenant-wide (reads
              useEstateAssets, same as EstateAssetsCard below), so it needs no
              trustId in scope and renders nothing itself when the estate is
              genuinely empty (see estate-split-donuts.tsx). */}
          <SectionErrorBoundary label="Personal vs trust">
            <EstateSplitDonuts />
          </SectionErrorBoundary>

          {/* Row 1-roles: POR-01 (M1 §H) — the role distribution. "Opening a client
              focuses one trust. At twenty clients that collapses." The count is in
              the headline above and the value is in the hero; what was missing on
              this screen is WHERE the person stands in each trust: founder of one,
              trustee of another, beneficiary of a third. The component that answers
              it has existed since 18 August and was mounted on the profile screen
              alone, which is not where anyone lands.

              NOT RENDERED WHILE A FIRM IS ACTING AS THIS CLIENT, on purpose.
              `GET /v1/me/trust-roles` derives its identity key from `claims.sub`,
              and an act-as token keeps the FIRM OPERATOR's sub (that is how the
              audit trail attributes the work). Mounted unguarded, a firm opening a
              client would read a card headed "Your trusts" listing the operator's
              OWN roles on the client's dashboard: the posture-leakage class again.
              The firm-side half of this row needs a client-scoped roles read, which
              does not exist. */}
          {!actAsClientName ? (
            <SectionErrorBoundary label="Your trusts">
              <div className="row">
                <YourTrustsCard />
              </div>
            </SectionErrorBoundary>
          ) : null}

          {/* Row 1a: Registration journey — the day-1 "where is my trust in the
              process" rail. Sits directly under the hero and reads from data
              already on this page. Only shown once a trust is in scope. */}
          {trustId ? (
            <SectionErrorBoundary label="Registration progress">
              <div className="row">
                <RegistrationJourney
                  trustId={trustId}
                  trust={firstTrust}
                  ficaDaysOut={reviewDaysOut}
                  initialFicaOnFile={summary?.initialFicaOnFile ?? null}
                />
              </div>
            </SectionErrorBoundary>
          ) : null}

          {/* Row 1a-2: Estate assets (ESTATE ownership model, 2026-07-11) — the
              ONE card that needs no trust in scope at all, so an estate-only
              client (zero trusts, everything person_owned) still sees their own
              assets. Un-wrapped (like ValuesAttentionCard below) — the
              component owns its own "row" div and renders NEITHER when there's
              nothing outside a trust to show (house empty-state idiom), so an
              empty row never leaves a phantom gap. */}
          <SectionErrorBoundary label="Personal assets">
            <EstateAssetsCard />
          </SectionErrorBoundary>

          {collapseInvestment ? (
            // Day-1 collapse: one honest line stands in for the whole investment-
            // reporting surface until an asset registers to the trust.
            <SectionErrorBoundary label="Investment reporting">
              <div className="row">
                {/* One honest line, now in the shape every other empty state on
                    the product has. The two reasons are still two different
                    sentences: "there is nothing yet" is not "this is not yours
                    to see", and telling a beneficiary that reporting "unlocks
                    when assets register" would be a false promise about a trust
                    that may already hold millions. */}
                <StateCard
                  variant="empty"
                  testId="investment-collapsed"
                  label="Investment reporting"
                  headline={
                    collapseForTier
                      ? 'The trust’s assets and valuations are held by its trustees.'
                      : 'Investment reporting unlocks when assets register to the trust.'
                  }
                  detail={
                    collapseForTier
                      ? 'You can see what has been distributed to you.'
                      : undefined
                  }
                />
              </div>
            </SectionErrorBoundary>
          ) : (
            /* Row 1b: Total value in the chosen DISPLAY currency (#43). Own card,
               independent of the Hero — demonstrates the ZAR/USD-indicative toggle
               without touching any other dashboard surface. */
            <SectionErrorBoundary label="Total value">
              <div className="row">
                <Suspense fallback={null}>
                  <DisplayTotalCard />
                </Suspense>
              </div>
            </SectionErrorBoundary>
          )}

          {/* Row 1c: Wealth distribution (B12) — the owner's estate split by
              asset class (property / vehicles / investments) as an inline-SVG
              donut off the trust's real recognised assets. Honest empty state
              until the first asset is recorded, so the wealth area is never bare. */}
          {/* `financials` — an asset-class split of the corpus. */}
          {canFinancials ? (
            <SectionErrorBoundary label="Wealth distribution">
              <div className="row">
                <WealthDistribution trustId={trustId} />
              </div>
            </SectionErrorBoundary>
          ) : null}
        </ConsoleBand>

        {/* Row 2: Resolution banner. `governance` — trustee deliberations and
            the signatures they await are not beneficiary business. */}
        {canGovernance ? (
          <ConsoleBand id="governance" title="Governance">
            <SectionErrorBoundary label="Resolutions">
              <div className="row">
                <ResolutionBanner />
              </div>
            </SectionErrorBoundary>
          </ConsoleBand>
        ) : null}

        <ConsoleBand
          id="the-book"
          title="The book"
          note={bookBandNote}
        >
          {/* Row 2b: Values needing attention (Valuations Wave 1, F4) — the
              staleness nudge. Renders nothing (not even the row) when every
              value is current, so no phantom spacing. */}
          {/* `financials` — a staleness nudge about valuations the caller
              cannot see is noise at best. */}
          {canFinancials ? (
            <SectionErrorBoundary label="Valuations">
              <ValuesAttentionCard trustId={trustId} />
            </SectionErrorBoundary>
          ) : null}

          {/* Row 3: Allocation + Quick actions. When the investment surface is
              collapsed (day-1, no holdings) the Allocation card folds into the
              quiet line above — but the Quick actions rail stays: it's how a
              client takes their next step, not zero-furniture. */}
          <SectionErrorBoundary label="Allocation & actions">
            {collapseInvestment ? (
              <div className="row">
                <QuickActions trustId={trustId} />
              </div>
            ) : (
              <div className="row r-2-1">
                <AllocationCard trustId={trustId} />
                <QuickActions trustId={trustId} />
              </div>
            )}
          </SectionErrorBoundary>

          {/* Row 4: Holdings table — full width. Collapsed on day-1 (no holdings). */}
          {!collapseInvestment && (
          <SectionErrorBoundary label="Holdings">
          <div className="row">
            <div
              className="card"
              aria-busy={holdingsQ.isLoading ? true : undefined}
            >
              <div className="card-head">
                {/* ROMAN, AND NO TRAILING FULL STOP (cohesion pass, 2026-09-15).
                    This was the contract's own named example: `Allocation.` and
                    `Holdings & performance.` were italic serif with full stops,
                    while a trust NAME was italic serif too, so italic carried no
                    meaning at all. Italic now means one thing, a named entity,
                    and a heading is roman and unpunctuated. The type comes from
                    `.card-title` in the stylesheet; the full stop was here. */}
                <h2 className="card-title">Holdings and performance</h2>
                <span className="card-meta">
                  {/* A2: never a count before the read settles. */}
                  {holdingsQ.isLoading
                    ? 'Reading the book…'
                    : holdingsQ.isError
                    ? 'The book could not be read'
                    : holdingsCount === 0
                    ? 'Nothing on record yet'
                    : `${holdingsCount} holding${holdingsCount === 1 ? '' : 's'}${
                        holdingsAsAt ? ` · valued ${holdingsAsAt}` : ''
                      }`}
                </span>
              </div>
              {/* THE FOUR STATES, THROUGH ONE COMPONENT. These were four
                  hand-rolled paragraphs, one of which printed "Loading holdings
                  for this trust..." as bare grey text on a card that already
                  had a layout. `StateCard` cannot show a number while loading
                  and cannot omit the reassurance on an error, which is the
                  point of it being a component rather than a convention. */}
              {!trustId ? (
                <StateCard
                  variant="empty"
                  testId="holdings-no-trust"
                  label="The book"
                  headline="Holdings appear here once a trust is onboarded."
                  detail="The opening balances are recorded during setup."
                />
              ) : holdingsQ.isLoading ? (
                <StateCard
                  variant="loading"
                  testId="holdings-loading"
                  label="The book"
                  headline="Reading this trust's holdings."
                />
              ) : holdingsQ.isError ? (
                <StateCard
                  variant="error"
                  testId="holdings-error"
                  label="The book"
                  headline="The holdings for this trust could not be read just now."
                  detail="The book refreshes on the next nightly snapshot once positions are recorded."
                  action={{ label: 'Try again', onClick: () => void holdingsQ.refetch() }}
                />
              ) : (
                <HoldingsTable
                  byAssetClass={holdingsQ.data?.byAssetClass ?? []}
                  totalMarketValueZar={holdingsQ.data?.totalMarketValueZar ?? 0}
                  trustId={trustId}
                  footerLink={
                    /* The contract's "open the full book" footer, and the only
                       door from this card to the register. Built here because
                       the shell that owns the router is here. */
                    <Link
                      to="/app/trusts/$id/portfolios"
                      params={{ id: trustId }}
                      style={{ color: 'var(--primary-700)', textDecoration: 'none' }}
                    >
                      Open the full book
                    </Link>
                  }
                />
              )}
            </div>
          </div>
          </SectionErrorBoundary>
          )}
        </ConsoleBand>

        {/* Row 5: Distributions — full width. Collapsed on day-1 (no holdings). */}
        {/* `distributions-all` — the eight-quarter rhythm is the trust's whole
            distribution history to EVERY beneficiary, not the caller's own.
            Their own payments live on the trust's Distributions tab, which the
            handler narrows to them. */}
        {!collapseInvestment && canDistributionsAll && (
          <ConsoleBand id="distributions" title="Distributions">
            <SectionErrorBoundary label="Distributions">
              <div className="row">
                <DistributionRhythm trustId={trustId} />
              </div>
            </SectionErrorBoundary>
          </ConsoleBand>
        )}

        {/* Row 6: Activity + Trustees + Beneficiaries.
            `governance` (the activity feed) and `people` (who the trustees and
            the other beneficiaries are). Rendered only when at least one is
            readable, and the grid narrows to the cards that survive so a
            beneficiary never gets a row with two empty thirds — a lopsided gap
            reads as a failed load, which is the impression this whole change
            exists to remove. */}
        {canGovernance || canPeople ? (
          <ConsoleBand id="the-record" title="The record">
            <SectionErrorBoundary label="Activity, trustees & beneficiaries">
              <div
                className="row"
                style={{
                  gridTemplateColumns: canGovernance && canPeople
                    ? '1.4fr 1fr 1fr'
                    : canPeople
                      ? '1fr 1fr'
                      : '1fr',
                }}
              >
                {canGovernance ? <ActivityFeed /> : null}
                {canPeople ? <TrusteesCard /> : null}
                {canPeople ? <BeneficiariesMini /> : null}
              </div>
            </SectionErrorBoundary>
          </ConsoleBand>
        ) : null}

        <div className="footer-sig">
          <div>
            <span className="serif" style={{ fontStyle: 'italic', fontSize: 14 }}>{tenantTagline}</span>
            {tenantFirmName && (
              <span style={{ marginLeft: 12, color: 'var(--ink-4)' }}>{tenantFirmName}</span>
            )}
          </div>
          <div className="mono">
            {trustLetterheadLine(firstTrust)}
          </div>
        </div>
    </ScreenShell>
  );
}
